import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import MoleculeLoader from '../components/MoleculeLoader';
import { ArrowLeft, Calendar, User, BookOpen, Share2, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/blog/${slug}`);
        if (response.ok) {
          setArticle(await response.json());
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/storage/')) return `${API_URL}${url}`;
    return (url.startsWith('/uploads') || url.startsWith('/api/uploads')) ? `${API_URL}${url}` : url;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, text: article.excerpt, url: window.location.href });
      } catch {}
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <MoleculeLoader size={96} label="Loading research article…" />
    </div>
  );

  if (!article) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="font-heading font-semibold text-slate-900 mb-2">Article not found</h2>
        <Link to="/research-insights"><Button variant="outline">Back to Articles</Button></Link>
      </div>
    </div>
  );

  // Build BlogPosting schema
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.meta_title || article.title,
    "description": article.meta_description || article.excerpt,
    "image": getImageUrl(article.image_url),
    "datePublished": article.published_at || article.created_at,
    "dateModified": article.updated_at || article.published_at,
    "author": { "@type": "Organization", "name": article.author || "Genora Research Team" },
    "publisher": { "@type": "Organization", "name": "Genora Peptides", "url": "https://genorapeptides.com" }
  };

  // FAQ schema
  const faqSchema = article.faq?.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": article.faq.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  } : null;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt}
        keywords={article.meta_keywords || article.keyword_tags || 'peptide research'}
        image={getImageUrl(article.image_url)}
        type="article"
      />

      {/* Schema markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaMarkup) }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

      {/* Hero Image */}
      {article.image_url && (
        <div className="relative h-[35vh] min-h-[280px] max-h-[400px] bg-slate-900">
          <img
            src={getImageUrl(article.image_url)}
            alt={article.title}
            className="w-full h-full object-cover opacity-80"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/70" />
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="pt-8 pb-6">
          <Link to="/research-insights" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-700 mb-6" data-testid="back-to-articles">
            <ArrowLeft className="h-4 w-4" />
            Back to Research Insights
          </Link>

          {article.category && (
            <Badge className="mb-3 bg-teal-100 text-teal-800 border-teal-200" data-testid="article-category">{article.category}</Badge>
          )}

          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-testid="article-title">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(article.published_at || article.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              {article.author || 'Genora Research Team'}
            </span>
            <button onClick={handleShare} className="flex items-center gap-1 hover:text-teal-700 transition-colors">
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            <strong>Educational Content Only:</strong> This article is for informational purposes and does not
            constitute medical advice. All products mentioned are intended for laboratory research use only.
          </p>
        </div>

        {article.excerpt && (
          <p className="text-xl text-slate-600 leading-relaxed mb-8 font-medium">{article.excerpt}</p>
        )}

        {/* Article Content */}
        <article className="prose prose-slate prose-lg max-w-none mb-12" dangerouslySetInnerHTML={{ __html: article.content }} />

        <style>{`
          article.prose h1 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.75rem; font-weight: 700; color: #0f172a; margin-top: 2rem; margin-bottom: 1rem; }
          article.prose h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.5rem; font-weight: 700; color: #0f172a; margin-top: 2rem; margin-bottom: 1rem; }
          article.prose h3 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.25rem; font-weight: 600; color: #0f172a; margin-top: 1.5rem; margin-bottom: 0.75rem; }
          article.prose p { color: #475569; line-height: 1.75; margin-bottom: 1rem; }
          article.prose ul, article.prose ol { color: #475569; margin: 1rem 0; padding-left: 1.5rem; }
          article.prose li { margin-bottom: 0.5rem; }
          article.prose strong { color: #0f172a; font-weight: 600; }
          article.prose a { color: #0a66ff; text-decoration: underline; font-weight: 600; }
        `}</style>

        {/* FAQ Section */}
        {article.faq?.length > 0 && (
          <div className="mb-12 border-t border-slate-200 pt-8" data-testid="faq-section">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {article.faq.map((faq, i) => (
                <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-medium text-slate-900 pr-4">{faq.question}</span>
                    {openFaq === i ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Author Box */}
        <div className="border-t border-slate-200 pt-8 mb-12">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center">
              <User className="h-7 w-7 text-teal-700" />
            </div>
            <div>
              <p className="font-heading font-semibold text-slate-900">{article.author || 'Genora Research Team'}</p>
              <p className="text-sm text-slate-500">Research Team at Genora Peptides</p>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        {article.related_articles?.length > 0 && (
          <div className="mb-12" data-testid="related-articles">
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-6">Related Research</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {article.related_articles.map((ra) => (
                <Link key={ra.slug} to={`/research-insights/${ra.slug}`} className="group">
                  <div className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    {ra.image_url && (
                      <img src={getImageUrl(ra.image_url)} alt={ra.title} className="w-full h-36 object-cover" loading="lazy" />
                    )}
                    <div className="p-4">
                      {ra.category && <Badge variant="outline" className="text-xs mb-2">{ra.category}</Badge>}
                      <h4 className="font-medium text-slate-900 text-sm group-hover:text-teal-700 transition-colors line-clamp-2">{ra.title}</h4>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-slate-50 rounded-xl p-8 mb-12">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Continue Your Research</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/products"><Button className="bg-teal-700 hover:bg-teal-800 text-white">Browse Products</Button></Link>
            <Link to="/coas"><Button variant="outline">View COAs</Button></Link>
            <Link to="/calculator"><Button variant="outline">Dosing Calculator</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
